import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductType, type StocktakeDetail } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Drawer } from '@/components/ui/Drawer';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { apiErrorMessage } from '@/services/apiClient';
import { useCategoriesQuery } from '../api/inventory.api';
import { useOpenStocktakeMutation } from '../api/stocktake.api';

interface OpenStocktakeDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onOpened: (session: StocktakeDetail) => void;
}

/**
 * Opens a count sheet.
 *
 * The scope is stated up front rather than inferred afterwards, so a partial count is honest about
 * what it did and did not cover.
 */
export function OpenStocktakeDrawer({ isOpen, onClose, onOpened }: OpenStocktakeDrawerProps) {
  const { t } = useTranslation();

  const [name, setName] = useState('');
  const [scopeProductType, setScopeProductType] = useState('');
  const [scopeCategoryId, setScopeCategoryId] = useState('');
  const [includeZeroStock, setIncludeZeroStock] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = useOpenStocktakeMutation();
  const categories = useCategoriesQuery();

  useEffect(() => {
    if (!isOpen) return;

    setName('');
    setScopeProductType('');
    setScopeCategoryId('');
    setIncludeZeroStock(false);
    setError(null);
  }, [isOpen]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    open.mutate(
      {
        ...(name.trim() ? { name: name.trim() } : {}),
        ...(scopeProductType ? { scopeProductType: scopeProductType as ProductType } : {}),
        ...(scopeCategoryId ? { scopeCategoryId } : {}),
        ...(includeZeroStock ? { includeZeroStock: true } : {}),
      },
      {
        onSuccess: (session) => {
          onOpened(session);
          onClose();
        },
        onError: (mutationError) =>
          setError(apiErrorMessage(mutationError) ?? t('inventory.stocktake.openError')),
      },
    );
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title={t('inventory.stocktake.open')}>
      <form className="space-y-4" onSubmit={handleSubmit}>
        {error ? <Alert tone="error">{error}</Alert> : null}

        <Alert tone="info">{t('inventory.stocktake.openHint')}</Alert>

        <Input
          value={name}
          label={t('inventory.stocktake.fields.name')}
          placeholder={t('inventory.stocktake.namePlaceholder')}
          onChange={(event) => setName(event.target.value)}
        />

        <FormField label={t('inventory.stocktake.fields.scopeProductType')}>
          {({ id }) => (
            <Select
              id={id}
              options={[
                { value: '', label: t('inventory.stocktake.wholeBranch') },
                ...Object.values(ProductType).map((type) => ({
                  value: type,
                  label: t(`catalog.types.${type}`),
                })),
              ]}
              value={scopeProductType}
              onChange={(event) => setScopeProductType(event.target.value)}
            />
          )}
        </FormField>

        <FormField label={t('inventory.stocktake.fields.scopeCategory')}>
          {({ id }) => (
            <Select
              id={id}
              options={[
                { value: '', label: t('inventory.stocktake.allCategories') },
                ...(categories.data?.items ?? []).map((category) => ({
                  value: category.id,
                  label: category.name,
                })),
              ]}
              value={scopeCategoryId}
              onChange={(event) => setScopeCategoryId(event.target.value)}
            />
          )}
        </FormField>

        <label className="text-ink flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={includeZeroStock}
            onChange={(event) => setIncludeZeroStock(event.target.checked)}
          />
          <span>
            {t('inventory.stocktake.includeZeroStock')}
            <span className="text-muted block text-xs">
              {t('inventory.stocktake.includeZeroStockHint')}
            </span>
          </span>
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" isLoading={open.isPending}>
            {t('inventory.stocktake.open')}
          </Button>
        </div>
      </form>
    </Drawer>
  );
}
